'use client';

import { type FC, type JSX, useState } from 'react';

import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import { ChevronLeft } from '../Icons/Paths/ChevronLeft';
import { BC_ITEM_PROPS, BC_LIST_ELE_PROPS, BC_LIST_PROPS } from './Breadcrumbs.constants';
import type { Breadcrumb, BreadcrumbLinkProps, BreadcrumbsProps } from './Breadcrumbs.types';
import { DefaultLink, oneBased } from './Breadcrumbs.utils';

import styles from './Styles/Default.module.css';

export const Breadcrumbs: FC<BreadcrumbsProps> = ({
  breadcrumbs,
  LinkComponent,
  dataTestId,
  className,
  allowLastItemAsLink,
  hasCustomShowHide = false,
  allowToggleHidden = true,
}) => {
  const [isFullView, setIsFullView] = useState(false);

  if (breadcrumbs.length === 0) {
    return null;
  }

  const Link = (LinkComponent || DefaultLink) as (props: BreadcrumbLinkProps) => JSX.Element;

  const firstItem = breadcrumbs[0];
  const lastItem = breadcrumbs[breadcrumbs.length - 1];
  const canShowFirst = firstItem?.link && !hasCustomShowHide;
  const canShowLast = !hasCustomShowHide;
  const hiddenBreadcrumbs = hasCustomShowHide
    ? breadcrumbs.filter(breadcrumb => breadcrumb.hide)
    : breadcrumbs.slice(1, -1);
  const customVisibleBreadcrumbs = breadcrumbs.filter(breadcrumb => !breadcrumb.hide);

  const toggleHiddenBreadcrumbs = () => {
    setIsFullView(!isFullView);
  };

  return (
    <ol
      className={cn(styles.bcWrpr, className, isFullView && styles.fullView)}
      data-test-id={dataTestId}
      {...BC_LIST_PROPS}
    >
      {canShowFirst && (
        <li className={styles.bcItm} key={`${firstItem.title}-${0}`} {...BC_LIST_ELE_PROPS}>
          {firstItem.showChevron && <ChevronLeft />}
          <Link
            className={styles.bcLink}
            link={firstItem.link}
            itemID={firstItem.link}
            {...BC_ITEM_PROPS}
          >
            <span itemProp="name">{firstItem.title}</span>
          </Link>
          <meta itemProp="position" content="1" />
        </li>
      )}

      {hiddenBreadcrumbs.length > 0 && (
        <li
          className={`${styles.bcItm} ${styles.ellip}`}
          {...(allowToggleHidden && { onClick: toggleHiddenBreadcrumbs })}
        >
          &#8230;
        </li>
      )}

      {hiddenBreadcrumbs.map((breadcrumb, i) => {
        return (
          <li
            className={`${styles.bcItm} ${styles.fullView} ${styles.treeItm}`}
            key={`${uuidv4()}`}
            {...BC_LIST_ELE_PROPS}
          >
            <Link
              className={styles.bcLink}
              link={breadcrumb.link}
              itemID={breadcrumb.link}
              {...BC_ITEM_PROPS}
            >
              <span itemProp="name">{breadcrumb.title}</span>
            </Link>
            {/* index plus 1 to make it 1 based, then add 1 for the firstItem */}
            <meta itemProp="position" content={`${canShowFirst ? oneBased(i) + 1 : oneBased(i)}`} />
          </li>
        );
      })}

      {hasCustomShowHide &&
        customVisibleBreadcrumbs.map((breadcrumb, i) => {
          return (
            <li className={styles.bcItm} key={`${breadcrumb.title}-${i}`} {...BC_LIST_ELE_PROPS}>
              <Link
                className={styles.bcLink}
                link={breadcrumb.link}
                itemID={breadcrumb.link}
                {...BC_ITEM_PROPS}
              >
                <span itemProp="name">{breadcrumb.title}</span>
              </Link>
              {/* There is no firstItem if hasCustomShowHide */}
              <meta itemProp="position" content={`${hiddenBreadcrumbs.length + oneBased(i)}`} />
            </li>
          );
        })}

      {canShowLast && (
        <li className={styles.bcItm} key={lastItem.title} {...BC_LIST_ELE_PROPS}>
          {allowLastItemAsLink && lastItem.link ? (
            <Link
              className={styles.bcLink}
              link={lastItem.link}
              itemID={lastItem.link}
              {...BC_ITEM_PROPS}
            >
              <span itemProp="name">{lastItem.title}</span>
            </Link>
          ) : (
            <span className={styles.active} itemProp="name">
              {lastItem.title}
            </span>
          )}
          <meta
            itemProp="position"
            content={`${
              canShowFirst ? hiddenBreadcrumbs.length + 2 : hiddenBreadcrumbs.length + 1
            }`}
          />
        </li>
      )}
    </ol>
  );
};

Breadcrumbs.displayName = 'Breadcrumbs';

export type { Breadcrumb };
